// src/App.jsx
import { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import ProtectedRoute from "./components/auth/ProtectedRoute";
import OnboardingModal, { shouldShowOnboarding } from "./components/onboarding/OnboardingModal";

import LandingPage        from "./pages/landing/LandingPage";
import LoginPage          from "./pages/auth/LoginPage";
import RegisterPage       from "./pages/auth/RegisterPage";
import ForgotPasswordPage from "./pages/auth/ForgotPasswordPage";
import DashboardPage      from "./pages/dashboard/DashboardPage";
import ProfilePage        from "./pages/profile/ProfilePage";
import JobsPage           from "./pages/jobs/JobsPage";
import LocalGigsAdmin     from "./pages/admin/LocalGigsAdmin";
import AdminDashboard     from "./pages/admin/AdminDashboard";
import CreditsPage              from "./pages/credits/CreditsPage";
import CreditsPurchaseReturnPage from "./pages/credits/CreditsPurchaseReturnPage";

function RootRoute() {
  const { currentUser } = useAuth();
  return currentUser ? <Navigate to="/dashboard" replace /> : <LandingPage />;
}

function OnboardingGate() {
  const { currentUser } = useAuth();
  const [show, setShow] = useState(() => currentUser && shouldShowOnboarding());

  useEffect(() => {
    const handler = () => setShow(currentUser && shouldShowOnboarding());
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, [currentUser]);

  // Show when user logs in fresh
  useEffect(() => {
    if (currentUser) setShow(shouldShowOnboarding());
  }, [currentUser]);

  if (!show) return null;
  return <OnboardingModal />;
}

function NotFoundPage() {
  return (
    <div style={{
      minHeight: "100vh", display: "flex", flexDirection: "column",
      alignItems: "center", justifyContent: "center",
      background: "var(--color-bg)", color: "var(--color-text)",
      fontFamily: "var(--font-body)", gap: 16,
    }}>
      <span style={{ fontSize: 48 }}>🔍</span>
      <h1 style={{ fontFamily: "var(--font-display)", fontSize: 28 }}>Page not found</h1>
      <p style={{ color: "var(--color-text-muted)" }}>That URL doesn't exist.</p>
      <a href="/dashboard" style={{
        background: "var(--color-accent)", color: "#0A0C14",
        padding: "10px 24px", borderRadius: 6,
        fontWeight: 700, textDecoration: "none", marginTop: 8,
      }}>Go to Dashboard</a>
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <ThemeProvider>
      <AuthProvider>
        <OnboardingGate />
        <Routes>
          {/* Public */}
          <Route path="/"                element={<RootRoute />} />
          <Route path="/login"           element={<LoginPage />} />
          <Route path="/register"        element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />

          {/* Authenticated */}
          <Route element={<ProtectedRoute />}>
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/profile"   element={<ProfilePage />} />
            <Route path="/jobs"      element={<JobsPage />} />
            <Route path="/credits"         element={<CreditsPage />} />
            <Route path="/credits/success" element={<CreditsPurchaseReturnPage />} />
          </Route>

          {/* Admin only */}
          <Route element={<ProtectedRoute requiredRole="admin" />}>
            <Route path="/admin/dashboard"  element={<AdminDashboard />} />
            <Route path="/admin/local-gigs" element={<LocalGigsAdmin />} />
          </Route>

          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}